import { cn } from "@tellescope/utils";
import type { TellescopeFormFieldBaseProps } from "../types";
import { Label } from "@tellescope/design-system";
import { NumberInput } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { FieldDescription } from "../FieldDescription";

export interface HeightFieldProps extends TellescopeFormFieldBaseProps<"Height"> {}

const displayedSubValue = (subValue: number | undefined) => (Number.isNaN(subValue) ? undefined : subValue);

export const HeightField = ({
  title,
  description,
  htmlDescription,
  value,
  onChange,
  className,
  error,
  onBlur,
  touched,
  disabled,
  required,
  autoFocus,
}: HeightFieldProps) => {
  const { t } = useTranslation("forms");
  const showError = !!(error && touched);

  return (
    <div className="space-y-1">
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <div className={cn("flex gap-2", className)}>
        <NumberInput
          value={value?.feet === 0 ? undefined : displayedSubValue(value?.feet)}
          onChange={(feet) => onChange({ feet: feet ?? NaN, inches: value?.inches ?? NaN })}
          onBlur={onBlur}
          placeholder={t("Feet")}
          precisionOptions={{ maxDecimalDigitCount: 0 }}
          className={cn(showError && "border-destructive")}
          disabled={disabled}
          autoFocus={autoFocus}
        />
        <NumberInput
          value={displayedSubValue(value?.inches)}
          onChange={(inches) => onChange({ feet: value?.feet ?? NaN, inches: inches ?? NaN })}
          onBlur={() => {
            if (value?.inches && value.inches > 11) {
              const carriedFeet = Number.isNaN(value.feet) ? 0 : value.feet ?? 0;
              onChange({
                feet: Math.floor(value.inches / 12) + carriedFeet,
                inches: value.inches % 12,
              });
            }
            onBlur?.();
          }}
          placeholder={t("Inches")}
          precisionOptions={{ maxDecimalDigitCount: 0 }}
          className={cn(showError && "border-destructive")}
          disabled={disabled}
        />
      </div>
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
